<template>
  <div
    class="filters-wrap rounded-lg p-3 border"
    :class="showFilters ? 'bg-green-50 border-green-200' : 'border-transparent'"
  >
    <div class="flex flex-wrap items-start gap-5">
      <!-- The toggle lives above Search rather than beside it - it takes the
           place Search's own (invisible, alignment-only) label used to
           occupy, so nothing new is added to the row's width. Collapsed,
           this column shrinks to just the button since nothing else renders
           inside it. -->
      <div class="flex flex-col" :class="{ 'min-w-[300px]': showFilters }">
        <!-- Pinned to the toggle's height so Clear All appearing beside it never pushes
             Search down; the 300px min-width leaves room for both buttons. -->
        <div class="flex items-center gap-2 h-7 mb-[5px]">
          <LlSecondaryButton
            variant="outlined"
            size="small"
            class="text-capitalize flex-shrink-0 !h-7"
            prepend-icon="mdi-filter-variant"
            :append-icon="showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            aria-label="Toggle filters"
            data-cy="matters-filters-toggle"
            @click="showFilters = !showFilters"
          >
            {{ showFilters ? '' : 'Filters' }}
          </LlSecondaryButton>
          <LlSecondaryButton
            v-if="showFilters && activeFilterCount > 0"
            variant="text"
            size="small"
            color="error"
            prepend-icon="mdi-close"
            class="!h-7"
            data-cy="matters-filters-clear-all-quiet"
            @click="handleClearAll"
          >
            Clear All
          </LlSecondaryButton>
        </div>

        <template v-if="showFilters">
          <!-- No top margin here, matching MatterInvolvingFilter/CustomFieldsFilter's
               own row-1-to-row-2 spacing (their label sits directly above their
               control with no gap) - the toggle button above is forced to that
               same label's height (!h-7) so this row starts level with theirs. -->
          <LlTextField
            v-model="filters.search"
            placeholder="Search Matters"
            prepend-inner-icon="mdi-magnify"
            hide-details
            @update:model-value="handleSearch"
          />
          <!-- Hidden until "Validate QuickBooks Connection" (table action bar)
               has been run at least once - matches the legacy matters-next
               page, where this dropdown (and the QBO column) only ever
               appear once the per-matter connection check has actually been
               requested. -->
          <div v-if="filters.checkQuickbooks" class="mt-2">
            <LlSelect
              v-model="filters.qbConnectionType"
              :items="qbConnectionOptions"
              item-title="title"
              item-value="value"
              :return-object="false"
              density="compact"
              variant="outlined"
              hide-details
              :loading="loading"
              @update:model-value="applyFilters"
            />
          </div>
          <LlCheckbox
            v-model="filters.archived"
            label="Show Archived"
            density="compact"
            class="mt-2"
            @update:model-value="applyFilters"
          />
        </template>
      </div>

      <!-- Collapsed: a lightweight summary in place of the fields below -
           the count and Clear All only earn their keep while the fields
           themselves aren't visible to answer the same questions. -->
      <div v-if="!showFilters" class="flex items-center gap-3">
        <LlStatusBadge v-if="activeFilterCount > 0">
          {{ activeFilterCount }} {{ activeFilterCount === 1 ? 'filter is' : 'filters are' }} active
        </LlStatusBadge>
        <LlSecondaryButton
          v-if="activeFilterCount > 0"
          variant="text"
          size="small"
          color="error"
          prepend-icon="mdi-close"
          data-cy="matters-filters-clear-all"
          @click="handleClearAll"
        >
          Clear All
        </LlSecondaryButton>
      </div>

      <template v-else>
        <div class="min-w-[240px] involving-filter-wrap">
          <MatterInvolvingFilter
            :form="filters"
            :horizontal="true"
            @matterInvolvingChange="handleMatterInvolvingChange"
          />
        </div>

        <ClientsFilter :filters="filters" @apply-filters="applyFilters" />

        <CustomFieldsFilter
          :fields="customFields"
          :model-value="filters.matterCustomFields"
          @update:model-value="handleCustomFieldsChange"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import LlTextField from '@/components/LlTextField.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';
import MatterInvolvingFilter from '@/components/MatterInvolvingFilter.vue';
import CustomFieldsFilter from '@/components/CustomFieldsFilter.vue';
import ClientsFilter from './ClientsFilter.vue';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';
import { useCustomFieldsStore } from '@/modules/settings/company/CustomFields/useCustomFieldsStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { filterableCustomFields } from '@/utils/customFieldFilterHelpers';
import type { CustomFieldFilterSelections, FilterableCustomField } from '@/models/customFieldFilterTypes';
import type { MattersFilters } from './useMattersFilters';

const props = defineProps({
  filters: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  activeFilterCount: { type: Number, default: 0 },
});

const emit = defineEmits(['apply-filters', 'clear-filters']);

// Visible by default so nothing changes for anyone who never touches the
// toggle - matches Time Entries' same default.
const showFilters = ref(true);

const handleClearAll = () => {
  emit('clear-filters');
};

const meStore = useMeStore();
const { me } = storeToRefs(meStore);
const accountsStore = useAccountsStore();
const { accountsList } = storeToRefs(accountsStore);

const qbConnectionOptions = [
  { title: 'QuickBooks Connection: Any', value: 'any' },
  { title: 'QuickBooks Connection: Good', value: 'good' },
  { title: 'QuickBooks Connection: Bad', value: 'bad' },
  { title: 'QuickBooks Connection: Not connected', value: 'none' },
];

const applyFilters = () => {
  props.filters.page = 1;
  emit('apply-filters');
};

// 300ms (Fixed Fees/Time Entries' debounce) fired mid-word for normal typing
// pace; 1000ms felt sluggish. 500ms is the middle ground.
let searchTimeout: ReturnType<typeof setTimeout>;
const handleSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(applyFilters, 500);
};

const { handleError } = useSnackbarHandler();
const customFieldsStore = useCustomFieldsStore();
const customFields = ref<FilterableCustomField[]>([]);

const loadCustomFields = async () => {
  try {
    customFields.value = filterableCustomFields(await customFieldsStore.listCustomFields(), 'matter');
  } catch (error) {
    handleError(error);
  }
};

const handleCustomFieldsChange = (selections: CustomFieldFilterSelections) => {
  props.filters.matterCustomFields = selections;
  applyFilters();
};

const handleMatterInvolvingChange = (
  updatedForm: Pick<MattersFilters, 'scopeAccount' | 'originating' | 'primary' | 'reviewer'>,
) => {
  props.filters.scopeAccount = updatedForm.scopeAccount;
  props.filters.originating = updatedForm.originating || false;
  props.filters.primary = updatedForm.primary || false;
  props.filters.reviewer = updatedForm.reviewer || false;
  props.filters.involved = props.filters.scopeAccount
    ? !(props.filters.originating || props.filters.primary || props.filters.reviewer)
    : false;
  applyFilters();
};

// Matches matters-next: Operators/Accountants see everything by default
// (empty scope); every other role defaults to their own matters where
// they're the responsible attorney.
onMounted(async () => {
  if (!me.value?.id) {
    await meStore.getMe();
  }
  if (me.value?.features?.customFieldFilters) {
    loadCustomFields();
  }
  if (!accountsList.value?.length) {
    await accountsStore.listAccounts();
  }

  const role = me.value?.role;
  if (!role || role === 'operator' || role === 'accountant') return;

  const currentUserAccount = accountsList.value?.find((acc) => acc.id === me.value.id);
  if (!currentUserAccount) return;

  props.filters.scopeAccount = currentUserAccount;
  props.filters.primary = true;
  props.filters.involved = false;
  applyFilters();
});
</script>

<style scoped>
/* LlTextField forwards $attrs to its underlying v-text-field, but LlSelect's
   root is a plain wrapping div so a passthrough bg-color prop would land on
   the div instead of the field - targeting Vuetify's shared field wrapper
   class directly works for both (and for MatterInvolvingFilter's own
   select) regardless of that difference. Without this, the green filter
   panel background bleeds through around the field icons. */
:deep(.v-field) {
  background-color: white;
}

/* Vuetify's unchecked checkbox icon is a single-color outline glyph with a
   transparent interior, so it blends into this panel's green tint - give it
   a white backing so it reads as a checkbox rather than a checkmark that
   appears/disappears. Scoped to the unchecked state only (Vuetify tags the
   checked one with --dirty) since the checked icon's own green fill already
   contrasts fine and doesn't need this. Reaches into LlCheckbox/
   MatterInvolvingFilter's rendered output the same way :deep(.v-field) above
   does - those are shared components, so this stays page-scoped here rather
   than changing their look everywhere they're used. */
:deep(.v-selection-control:not(.v-selection-control--dirty) .v-icon) {
  background-color: white;
  border-radius: 2px;
}

/* MatterInvolvingFilter's own select doesn't grow to fill its container on
   its own (its underlying Vuetify field renders with a v-input--horizontal
   layout that sizes to content instead of stretching) - force it to fill
   the width set on .involving-filter-wrap above. Scoped to .v-autocomplete
   specifically, NOT the generic .v-input - that class is shared by every
   Vuetify form control including the Responsible/Originating/Reviewer
   checkboxes below, and forcing THEM to width:100% stretched the checkbox
   itself and pushed its separate label sibling out of place. */
.involving-filter-wrap :deep(.tw),
.involving-filter-wrap :deep(.v-autocomplete),
.involving-filter-wrap :deep(.v-field) {
  width: 100%;
}
</style>
